import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  MeetingDetail,
  MeetingListItem,
  CreateMeetingPayload,
  UpdateMeetingPayload,
  PaginatedResponse,
} from "../types";

const MEETINGS_KEY = ["meetings"] as const;

export function useMeetingList(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    type?: string;
    companyId?: string;
    attendeeCompanyId?: string;
    attendeeContactId?: string;
    productId?: string;
    projectId?: string;
    phaseId?: string;
    stageId?: string;
    dateFrom?: string;
    dateTo?: string;
    enabled?: boolean;
  } = {},
): UseQueryResult<PaginatedResponse<MeetingListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...MEETINGS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && params.enabled !== false,
    queryFn: () =>
      apiFetch<PaginatedResponse<MeetingListItem>>("/meetings", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          type: params.type,
          companyId: params.companyId,
          attendeeCompanyId: params.attendeeCompanyId,
          attendeeContactId: params.attendeeContactId,
          productId: params.productId,
          projectId: params.projectId,
          phaseId: params.phaseId,
          stageId: params.stageId,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
        },
      }),
  });
}

export function useMeeting(id: string): UseQueryResult<MeetingDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...MEETINGS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ meeting: MeetingDetail }>(
        `/meetings/${id}`,
        { token: accessToken },
      );
      return res.meeting;
    },
  });
}

export function useCreateMeeting() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMeetingPayload) =>
      apiFetch<{ meeting: MeetingDetail }>("/meetings", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUpdateMeeting(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateMeetingPayload) =>
      apiFetch<{ meeting: MeetingDetail }>(`/meetings/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY] });
    },
  });
}

export function useUpdateMeetingStatus() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      apiFetch<{ meeting: MeetingDetail }>(`/meetings/${id}/status`, {
        method: "PUT",
        body: { status },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY] });
    },
  });
}

export function useUpdateMeetingNotes(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notes: string | null) =>
      apiFetch<{ meeting: MeetingDetail }>(`/meetings/${id}/notes`, {
        method: "PUT",
        body: { notes },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, "detail", id] });
    },
  });
}

export function useUpdateMeetingAgenda(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agenda: string | null) =>
      apiFetch<{ meeting: MeetingDetail }>(`/meetings/${id}/agenda`, {
        method: "PUT",
        body: { agenda },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, "detail", id] });
    },
  });
}

export function useAddAttendees(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { userIds?: string[]; contactIds?: string[] }) =>
      apiFetch<{ meeting: MeetingDetail }>(`/meetings/${id}/attendees`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, "detail", id] });
    },
  });
}

export function useRemoveAttendee(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attendeeId: string) =>
      apiFetch(`/meetings/${id}/attendees/${attendeeId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, "detail", id] });
    },
  });
}

export function useUpdateAttendance(meetingId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      attendeeId,
      hasAttended,
    }: {
      attendeeId: string;
      hasAttended: boolean;
    }) =>
      apiFetch(
        `/meetings/${meetingId}/attendees/${attendeeId}/attendance`,
        {
          method: "PUT",
          body: { hasAttended },
          token: accessToken,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY, "detail", meetingId] });
    },
  });
}

export function useDeleteMeeting() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/meetings/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...MEETINGS_KEY] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useMeetingSubEntity<T>(
  meetingId: string,
  entity: string,
  params: Record<string, string | number | undefined> = {},
) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...MEETINGS_KEY, meetingId, entity, params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!meetingId,
    queryFn: () =>
      apiFetch<PaginatedResponse<T>>(`/meetings/${meetingId}/${entity}`, {
        token: accessToken,
        query: params as Record<string, string | number>,
      }),
  });
}
